/**
 * Position DSL Parser
 *
 * Supports various position formats:
 * - "first" - Start from the beginning
 * - "last" - Start/end at the last message
 * - "tail" - Live streaming (end only)
 * - "100" - Absolute sequence number 100
 * - "-100" or "-100b" - 100 blocks/messages from the end
 * - "-5m" - 5 minutes ago
 * - "-1h" - 1 hour ago
 * - "-30s" - 30 seconds ago
 * - "-2d" - 2 days ago
 * - "-20b|-10m" - Either 20 blocks OR 10 minutes from end, whichever gives MORE messages
 * - "+100" or "+100b" - 100 blocks/messages from the start
 */

export interface PositionSpec {
  type: 'keyword' | 'absolute' | 'relative-end' | 'relative-start' | 'combined';
  keyword?: 'first' | 'last' | 'tail';
  absoluteSeq?: number;
  offset?: number;
  timeAgo?: string;
  // For combined type: array of specs to resolve and pick from
  specs?: Array<{ offset?: number; timeAgo?: string }>;
}

/**
 * Parse a position string into a structured spec
 */
export function parsePosition(pos: string | number | undefined): PositionSpec | undefined {
  if (pos === undefined) {
    return undefined;
  }

  // Handle numeric input (absolute sequence number)
  if (typeof pos === 'number') {
    return { type: 'absolute', absoluteSeq: pos };
  }

  const str = pos.trim();

  // Keywords
  if (str === 'first' || str === 'last' || str === 'tail') {
    return { type: 'keyword', keyword: str };
  }

  // Check for combined format: "-100b|-5m" or "-100|-5m" (offset | time, both from end)
  const combinedMatch = str.match(/^-(\d+)b?\|-([0-9]+[smhd])$/);
  if (combinedMatch) {
    return {
      type: 'combined',
      specs: [
        { offset: parseInt(combinedMatch[1], 10) },
        { timeAgo: combinedMatch[2] }
      ]
    };
  }

  // Check for relative to end with offset: "-100" or "-100b"
  const relativeEndOffsetMatch = str.match(/^-(\d+)b?$/);
  if (relativeEndOffsetMatch) {
    return {
      type: 'relative-end',
      offset: parseInt(relativeEndOffsetMatch[1], 10)
    };
  }

  // Check for relative to end with time: "-5m", "-1h", "-30s", "-2d"
  const relativeEndTimeMatch = str.match(/^-([0-9]+)([smhd])$/);
  if (relativeEndTimeMatch) {
    return {
      type: 'relative-end',
      timeAgo: relativeEndTimeMatch[1] + relativeEndTimeMatch[2]
    };
  }

  // Check for relative to start: "+100" or "+100b"
  const relativeStartMatch = str.match(/^\+(\d+)b?$/);
  if (relativeStartMatch) {
    return {
      type: 'relative-start',
      offset: parseInt(relativeStartMatch[1], 10)
    };
  }

  // Check for absolute sequence number (plain number)
  const absoluteMatch = str.match(/^(\d+)$/);
  if (absoluteMatch) {
    return {
      type: 'absolute',
      absoluteSeq: parseInt(absoluteMatch[1], 10)
    };
  }

  // If we can't parse it, return undefined
  return undefined;
}

/**
 * Validate a position string
 */
export function validatePosition(pos: string | number | undefined): boolean {
  if (pos === undefined) {
    return true; // undefined is valid (means not specified)
  }
  return parsePosition(pos) !== undefined;
}

/**
 * Get a hint for valid position formats
 */
export function getPositionHint(): string {
  return 'Valid formats: first, last, tail, 100 (absolute), -100b (blocks from end), -5m (time from end), -20b|-10m (combined), +100b (blocks from start)';
}

/**
 * Resolve a position spec to an actual sequence number using the API
 */
export async function resolvePosition(
  spec: PositionSpec | undefined,
  processUid: string,
  channel: string,
  authToken: string,
  hermesUrl: string,
  context: 'start' | 'end'
): Promise<number | undefined> {
  if (!spec) {
    return undefined;
  }

  // Handle absolute sequence numbers
  if (spec.type === 'absolute') {
    return spec.absoluteSeq;
  }

  // Handle keywords
  if (spec.type === 'keyword') {
    if (spec.keyword === 'tail' && context === 'end') {
      return undefined; // undefined means live streaming
    }
    if (spec.keyword === 'first' || spec.keyword === 'last') {
      // Need to fetch stream info to get min/max
      const { getStreamInfo } = await import('./api');
      const info = await getStreamInfo(processUid, channel, authToken, hermesUrl);
      return spec.keyword === 'first' ? info.minSeq : info.maxSeq;
    }
  }

  // Handle relative-to-start (not yet implemented in API, need stream info)
  if (spec.type === 'relative-start') {
    const { getStreamInfo } = await import('./api');
    try {
      const info = await getStreamInfo(processUid, channel, authToken, hermesUrl);
      return (info.minSeq ?? 0) + (spec.offset ?? 0);
    } catch (err) {
      // No stream for this run (404) → start at 0. The worker reports no logs.
      const { ContinuumApiHttpError } = await import('../../hermesclient/continuum-api-client');
      if (err instanceof ContinuumApiHttpError && err.status === 404) {
        return spec.offset ?? 0;
      }
      throw err;
    }
  }

  // Handle relative-to-end
  if (spec.type === 'relative-end') {
    const response = await fetch(`${hermesUrl}/api/continuum/channel_start_sequence`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Auth-Token': authToken,
      },
      body: JSON.stringify({
        processUid,
        channel,
        timeAgo: spec.timeAgo || '',
        offsetFromEnd: spec.offset || 0,
      }),
    });

    // 404 = no stream for this run (no console output). Not an error —
    // resolve to the start (0) and let the worker report "no logs".
    if (response.status === 404) {
      return 0;
    }
    if (!response.ok) {
      throw new Error(`Failed to resolve position: ${response.statusText}`);
    }

    const data = await response.json();
    return data.startSequence;
  }

  // Handle combined: resolve multiple specs and pick based on context
  if (spec.type === 'combined' && spec.specs) {
    const sequences: number[] = [];

    for (const subSpec of spec.specs) {
      const requestBody = {
        processUid,
        channel,
        timeAgo: subSpec.timeAgo || '',
        offsetFromEnd: subSpec.offset || 0,
      };

      const response = await fetch(`${hermesUrl}/api/continuum/channel_start_sequence`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': authToken,
        },
        body: JSON.stringify(requestBody),
      });

      // 404 = no stream for this run; treat as start (0), don't fail.
      if (response.status === 404) {
        sequences.push(0);
        continue;
      }
      if (!response.ok) {
        throw new Error(`Failed to resolve position: ${response.statusText}`);
      }

      const data = await response.json();
      sequences.push(data.startSequence);
    }

    // For start: pick earliest (min) to get MORE messages
    // For end: pick latest (max) to get MORE messages
    const result = context === 'start' ? Math.min(...sequences) : Math.max(...sequences);
    return result;
  }

  return undefined;
}
